import { useMutation } from '@tanstack/react-query';

import { getUser, signIn, signOut } from 'src/api/cms';
import { AuthErrorCode } from 'src/constants';
import { getErrorMessage } from 'src/helpers/error';
import { showMessage } from 'src/services/notify/notify';
import { queryClient } from 'src/services/react-query/query-client';
import { useUsersStore } from 'src/stores/users';

const usersState = useUsersStore.getState();

export const onUnauthorized: (withNotify?: boolean) => void = (withNotify = true) => {
  usersState.setUser(null);
  queryClient.clear();

  if (withNotify) {
    showMessage({
      type: 'error',
      content: 'Session has expired, please log in again!',
    });
  }
};

export const useIsUserLoggedIn = () => useUsersStore((state) => !!state.user);

export const useIsUserChecked = () => useUsersStore((state) => state.isUserChecked);

export const useUser = () => useUsersStore((state) => state.user);

export const useUserActions = () => ({
  setUser: usersState.setUser,
  getUser: async () => {
    try {
      const user = await getUser();
      usersState.setUser(user);
    } catch (err) {
      if (getErrorMessage(err) !== AuthErrorCode.UNAUTHORIZED) {
        showMessage({
          type: 'error',
          content: 'Server is unavailable, please contact administrator.',
        });
      }
    } finally {
      usersState.setIsUserChecked(true);
    }
  },
  signOut: async () => {
    await signOut();
    onUnauthorized(false);
  },
});

export const useSignInAction = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: signIn,
  });

  return {
    signIn: mutateAsync,
    isInProgress: isPending,
  };
};
